import React from "react";
import psl from "psl";
import isEmail from "validator/lib/isEmail";
import { Backdrop } from "@material-ui/core";
import Cookies from "js-cookie";
import firebase from "firebase/compat/app";

import getLogger, { LogGroup } from "js/core/logger";
import Api from "js/core/api";
import Analytics from "js/analytics";
import { CreateAccount, ResetPassword, SignIn, ResetDone, WaitForGoogle, WaitForMicrosoft, VerifyEmail } from "./AuthUi";
import { AuthConsumer } from "./AuthConsumer";
import { FirebaseSessionTracker } from "./FirebaseSessionTracker";
import { Experiment } from "../../components/Experiment";
import { FirebaseUserConsumer } from "./FirebaseUserContext";
import { RemoveSplashScreen, RestoreSplashScreen } from "js/core/SplashScreen";
import * as googleAuth from "js/core/oauth/googleAuth";
import { getGoogleAuthTokens } from "js/core/oauth/getGoogleAuthTokens";
import * as microsoftAuth from "js/core/oauth/microsoftAuth";
import { initializeAllExperiments } from "js/core/services/experiments";
import Spinner from "js/react/components/Spinner";
import { browserHistory } from "js/react/history";
import { removeQueryParam, addQueryParam } from "js/core/utilities/queryParams";

const logger = getLogger(LogGroup.AUTH);

class GoogleRedirectHandler extends React.Component {
    async componentDidMount() {
        const { signInWithGoogleIdToken, onError } = this.props;

        const params = new URLSearchParams(window.location.search);
        if (params.has("google_auth_success") && params.get("google_auth_success") === "true") {
            // token_key is no longer passed in URL, tokens are retrieved from localStorage
            const { idToken } = getGoogleAuthTokens();

            if (!idToken) {
                // Cookie not available (can be caused by Safari private mode blocking cookies)
                logger.error("[GoogleRedirectHandler] Google auth id token not found in localStorage");
                onError("Authentication failed: Unable to retrieve authentication token. Safari privacy protection may be in effect. Please try again or use email/password sign-in.");
                return;
            }

            // Signing in
            signInWithGoogleIdToken(idToken);
            return;
        }

        if (params.has("google_auth_error")) {
            // Cleanup
            removeQueryParam("google_auth_error");

            onError(params.get("google_auth_error"));
        }
    }

    render() {
        const { children } = this.props;
        return children;
    }
}

class MicrosoftRedirectHandler extends React.Component {
    componentDidMount() {
        const { signInWithMicrosoftIdToken, onError } = this.props;

        const params = new URLSearchParams(window.location.search);
        if (params.has("microsoft_auth_success") && params.get("microsoft_auth_success") === "true") {
            const customToken = localStorage.getItem("microsoft_auth_custom_token");
            if (customToken) {
                localStorage.removeItem("microsoft_auth_custom_token");
            }

            if (!customToken) {
                logger.error("[MicrosoftRedirectHandler] Microsoft auth custom token not found in localStorage");
                onError("Authentication failed: Unable to retrieve authentication token. Safari privacy protection may be in effect. Please try again or use email/password sign-in.");
                return;
            }

            // Signing in
            signInWithMicrosoftIdToken(customToken);
            return;
        }

        if (params.has("microsoft_auth_error")) {
            // Cleanup
            removeQueryParam("microsoft_auth_error");

            onError(params.get("microsoft_auth_error"));
        }
    }

    render() {
        const { children } = this.props;
        return children;
    }
}

export function Authenticate(props) {
    return (
        <AuthConsumer>
            {auth => (
                <FirebaseUserConsumer>
                    {currentUser => (
                        <AuthenticateImplementation {...props} auth={auth} currentUser={currentUser} />
                    )}
                </FirebaseUserConsumer>
            )}
        </AuthConsumer>
    );
}

class AuthenticateImplementation extends React.Component {
    constructor(props) {
        super(props);

        if (props.defaultPage != "signIn" && props.defaultPage != "createAccount") {
            throw new Error(
                "<Authenticate> `defaultPage` prop is required " +
                "(must be `signIn` or `createAccount`)"
            );
        }

        let error;
        let info;
        if (props.readonlyEmail) {
            if (props.presentationPermission === "private") {
                error = "This presentation is private.";
            }
        }

        this._isUnmounted = false;

        this.state = {
            error,
            info,
            loading: false,
            isReady: false,
            page: props.defaultPage || "signIn",
            email: props.readonlyEmail || "",
            password: "",
            ssoState: "none"
        };
    }

    componentDidMount = async () => {
        const { readonlyEmail } = this.props;

        // When the email is readonly, we need to check for SSO up front
        if (readonlyEmail) {
            this.checkEmail(readonlyEmail);
        }

        // Track the zoom app flag in analytics, so auth events can be tagged with it
        if (this.props.reason === "zoom-app") {
            Analytics.util.trackState({ isZoomApp: true });
        }

        this.setState({ isReady: true });
    }

    componentDidUpdate = (prevProps, prevState) => {
        if (prevProps.currentUser !== this.props.currentUser && !this.props.currentUser) {
            // Logged out
            this.setState({ page: "signIn" });
        }
    }

    componentWillUnmount = () => {
        this._isUnmounted = true;
    }

    handleSignIn = auth => (async () => {
        const { email, password } = this.state;
        try {
            this.setState({ loading: true, error: undefined, emailError: undefined, passwordError: undefined });

            if (email == "") {
                this.setState({ loading: false, emailError: "Required" });
                return;
            }

            // clicking sign-in an email but no password begins SSO (got this idea from Dropbox)
            if (email) {
                try {
                    const ssoState = this.state.ssoState;
                    if (ssoState === "strict" || (ssoState === "optional" && password === "")) {
                        window.location = `/api/sso/saml/login?email=${encodeURIComponent(email)}`;
                        return;
                    }
                } catch (err) {
                    this.setState({ loading: false, error: err.message });
                    return;
                }
            }

            const userCredential = await auth.signInWithEmailAndPassword(email, password);

            // Ensure all experiments are initialized for the user
            await initializeAllExperiments();

            // resume the loading animation, if any
            RestoreSplashScreen();

            const isNew = await this.ensureRegitered("email");
            if (isNew) {
                this.trackCreateAccount(userCredential.user, false);
            } else {
                this.trackLogin(userCredential.user);
            }

            if (this._isUnmounted) {
                return;
            }

            this.setState({ loading: false });
        } catch (err) {
            this.trackError(email, err);
            if (err.code == "auth/invalid-email") {
                this.setState({ loading: false, emailError: "Invalid email" });
            } else if (err.code == "auth/user-not-found") {
                this.setState({ loading: false, emailError: "No account for this email" });
            } else if (err.code == "auth/wrong-password") {
                this.setState({ loading: false, passwordError: password == "" ? "Required" : "Incorrect password" });
            } else {
                this.setState({ loading: false, error: err.message });
            }
        }
    })

    // note: for auth with Google, logging in and creating an account are the same thing
    handleGoogle = auth => (async () => {
        const { popup } = this.props;
        const { page: startPage } = this.state;

        this.setState({ page: "waitForGoogle", loading: true, error: undefined, isSigningInToGoogle: true });

        const scope = "profile email";

        if (!popup) {
            // Do proper redirect flow
            googleAuth.authenticate(scope, `${window.location.pathname}${window.location.search}`);
            return;
        }

        // Do fake popup (redirect flow in a child window)
        try {
            const { idToken } = await googleAuth.authenticateWithPopup(scope);
            this.handleGoogleIdToken(auth, idToken);
        } catch (err) {
            const error = err instanceof googleAuth.GoogleAuthFlowWasInterruptedError ? null : err.message;
            this.setState({ page: startPage, loading: false, error, isSigningInToGoogle: false });
        }
    })

    handleGoogleIdToken = async (auth, idToken) => {
        const { page: startPage } = this.state;

        this.setState({ page: "waitForGoogle", loading: true, error: undefined, isSigningInToGoogle: true });

        try {
            await this.signInWithGoogleIdToken(auth, idToken);
        } catch (err) {
            this.setState({ page: startPage, loading: false, error: err.message, isSigningInToGoogle: false });
        }
    }

    ensureRegitered = async provider => {
        logger.info("Calling registerUser()");

        const relativeUrl = window.location.href.substr(window.location.origin.length);
        const { isNew } = await Api.registerUser.post({ affiliate: this.props.affiliate, provider, referer: relativeUrl });

        logger.info(`User registered ${isNew ? "new" : "existing"}`);

        return isNew;
    }

    signInWithGoogleIdToken = async (auth, idToken) => {
        logger.info("Signing in to Firebase with Google auth credential");
        const credential = firebase.auth.GoogleAuthProvider.credential(idToken);
        const userCredential = await auth.signInWithCredential(credential);

        // resume the loading animation, if any
        RestoreSplashScreen();

        // first sign-in results in account creation
        const isNew = await this.ensureRegitered("google");
        if (isNew) {
            // Letting the consumers know that we're signing up a new user
            // we don't need to do that for email/password sign up because
            // it'll be set by the server upon email verification
            addQueryParam("signup", "true");

            this.trackCreateAccount(userCredential.user, true);
        } else {
            this.trackLogin(userCredential.user);
        }

        // Ensure all experiments are initialized for the user
        await initializeAllExperiments();

        if (this._isUnmounted) {
            return;
        }

        this.setState({ loading: false, isSigningInToGoogle: false });
    }

    // note: for auth with Microsoft, logging in and creating an account are the same thing
    handleMicrosoft = auth => (async () => {
        const { popup } = this.props;
        const { page: startPage } = this.state;

        this.setState({ page: "waitForMicrosoft", loading: true, error: undefined, isSigningInToMicrosoft: true });

        if (!popup) {
            // Do proper redirect flow
            microsoftAuth.authenticate(microsoftAuth.AUTH_SCOPES.join(" "), `${window.location.pathname}${window.location.search}`);
            return;
        }

        // Do fake popup (redirect flow in a child window)
        try {
            const { customToken } = await microsoftAuth.authenticateWithPopup(microsoftAuth.AUTH_SCOPES.join(" "));
            this.handleMicrosoftIdToken(auth, customToken);
        } catch (err) {
            const error = err instanceof microsoftAuth.MicrosoftAuthFlowWasInterruptedError ? null : err.message;
            this.setState({ page: startPage, loading: false, error, isSigningInToMicrosoft: false });
        }
    })

    handleMicrosoftError = error => {
        const existingUserUpdated = localStorage.getItem("microsoft_auth_existing_user_updated") === "true";

        if (localStorage.getItem("microsoft_auth_existing_user_updated")) {
            localStorage.removeItem("microsoft_auth_existing_user_updated");
        }

        if (existingUserUpdated) {
            browserHistory.push(`/account/me?microsoft_auth_error=${error}`);
        } else {
            this.setState({ error });
        }
    }

    handleMicrosoftIdToken = async (auth, customToken) => {
        const { page: startPage } = this.state;

        this.setState({ page: "waitForMicrosoft", loading: true, error: undefined, isSigningInToMicrosoft: true });

        try {
            await this.signInWithMicrosoftIdToken(auth, customToken);
        } catch (err) {
            this.setState({ page: startPage, loading: false, error: err.message, isSigningInToMicrosoft: false });
        }
    }

    signInWithMicrosoftIdToken = async (auth, customToken) => {
        logger.info("Signing in to Firebase with Microsoft auth credential");

        // Sign in with the custom token generated for the user
        const userCredential = await auth.signInWithCustomToken(customToken);

        // resume the loading animation, if any
        RestoreSplashScreen();

        // first sign-in results in account creation
        const isNew = await this.ensureRegitered("microsoft");
        if (isNew) {
            // Letting the consumers know that we're signing up a new user
            // we don't need to do that for email/password sign up because
            // it'll be set by the server upon email verification
            addQueryParam("signup", "true");

            this.trackCreateAccount(userCredential.user, true);
        } else {
            this.trackLogin(userCredential.user);
        }

        // Ensure all experiments are initialized for the user
        await initializeAllExperiments();

        if (this._isUnmounted) {
            return;
        }

        this.setState({ loading: false, isSigningInToMicrosoft: false });
    }

    handleCreateAccount = auth => (async () => {
        const { email, password } = this.state;

        try {
            this.setState({ loading: true, error: undefined });

            const userCredential = await auth.createUserWithEmailAndPassword(email, password);

            await this.ensureRegitered("email");

            this.trackCreateAccount(userCredential.user, false);

            // Ensure all experiments are initialized for the user
            await initializeAllExperiments();

            if (!this._isUnmounted) {
                this.setState({ loading: false });
            }
        } catch (err) {
            this.setState({ loading: false, error: err.message });
        }
    })

    trackCreateAccount = (user, isGoogleSignup) => {
        // whatever happens next is part of the "signup action"
        Analytics.util.trackState({
            "is_signing_up": true
        });

        Analytics.util.track({
            audit: true,
            category: "Auth",
            action: "signup",
            gtag: "conversion",
            props: {
                email: user.email,
                userId: user.uid,
                workspace_id: "all"
            }
        });

        Analytics.util.track({
            audit: true,
            category: "SignupFlow",
            action: "NavForward",
            props: {
                step_number: 1,
                step_name: "Account Creation",
                object: "button",
                object_label: "Continue",
                action: "clicked",
                workspace_id: "all"
            }
        });

        if (isGoogleSignup) {
            Analytics.util.track({
                audit: true,
                category: "SignupFlow",
                action: "NavForward",
                props: {
                    step_number: 2,
                    step_name: "Verification",
                    verification_type: "google",
                    object: "button",
                    object_label: "Continue with Google",
                    action: "clicked",
                    workspace_id: "all"
                }
            });
        }
    }

    trackLogin = user => {
        Analytics.util.track({
            audit: true,
            category: "Auth",
            action: "login",
            props: {
                email: user.email,
                userId: user.uid,
                workspace_id: "all"
            }
        });
    }

    trackError = (email, err) => {
        const sanitizedErrorCode = err.code.replace("auth/", "");
        Api.authAudit.post({
            namespace: "analytics",
            email,
            props: {
                category: "Auth",
                action: sanitizedErrorCode
            }
        });
    }

    handleSwitchForm = ({ signIn }) => {
        this.setState({
            page: signIn ? "signIn" : "createAccount",
            error: undefined,
            emailError: undefined,
            passwordError: undefined
        });
    }

    handleClickForgot = () => {
        this.setState({ page: "forgotPassword" });
    }

    handleResetPassword = auth => async () => {
        try {
            await auth.sendPasswordResetEmail(this.state.email);
            this.setState({ page: "resetDone" });
        } catch (err) {
            this.setState({ emailError: err.message });
        }
    }

    handleEmailChange = email => {
        this.setState({ email });
        return this.checkEmail(email);
    }

    handlePasswordChange = password => {
        this.setState({ password });
    }

    checkEmail = email => {
        if (this.ssoStatusCheckTimeout) {
            clearTimeout(this.ssoStatusCheckTimeout);
            this.ssoStatusCheckTimeout = null;
        }

        if (!isEmail(email)) {
            this.setState({ ssoState: "none" });
            return;
        }

        // parse the domain out of the email address
        const domain = email.split("@").pop();

        // check that the domain is includes an actual, listed TLD, this
        // prevents us from sending an excessive number of requests to our API
        const parsed = psl.parse(domain);
        if (parsed.sld && parsed.listed) {
            // debounce for common case of .co -> com
            this.ssoStatusCheckTimeout = setTimeout(async () => {
                try {
                    const { status } = await Api.ssoStatus.get({ email });
                    if (status === "strict") {
                        this.setState({ ssoState: status, page: "signIn" });
                        return;
                    }

                    this.setState({ ssoState: status });
                } catch (err) {
                    logger.error(err, "Api.ssoStatus.get() failed", { email });
                }
            }, 300);
            return;
        }

        this.setState({ ssoState: "none" });
    }

    handleFirstNameChange = value => {
        this.setState({
            firstName: value
        });
    }

    handleLastNameChange = value => {
        this.setState({
            lastName: value
        });
    }

    handleCompanyChange = value => {
        this.setState({
            company: value
        });
    }

    hero() {
        if (this.props.popup) {
            return null;
        }
        switch (this.props.reason) {
            case "presentation-invite":
                return {
                    image: "presentation-invite",
                    thumbnailUrl: this.props.presentationThumbnailUrl,
                    caption: (
                        <span>
                            <b>{this.props.presentationName}</b>
                            <br />
                            shared by {this.props.presentationSharedBy}
                        </span>
                    )
                };
            default:
                return { image: this.props.reason };
        }
    }

    subheading() {
        if (this.props.popup) {
            return "";
        } else if (this.props.reason == "team-invite") {
            return <span>Join your team, <b>{this.props.teamName}</b>, and start creating beautiful presentations faster.</span>;
        } else if (this.state.page === "signIn" && this.props.reason == "presentation-invite") {
            return "Sign in to access this presentation";
        } else if (this.state.page === "createAccount" && this.props.reason == "presentation-invite") {
            return "Create a free account to access this presentation";
        } else if (this.props.reason == "provisioning") {
            return `${this.state.page === "signIn" ? "Sign in" : "Create an account"} to use Beautiful.ai in ${this.props.provisioningSourceName}`;
        } else if (this.state.page === "signIn" && this.props.reason === "zoom-app") {
            return "Sign in to use Beautiful.ai in your Zoom meeting";
        }
        return undefined;
    }

    render() {
        const {
            noVerify,
            popup,
            skipReload,
            backdropVisible,
            onClose,
            auth,
            currentUser
        } = this.props;
        const { isReady } = this.state;

        if (!isReady) {
            return <Spinner />;
        }

        const isLoggedIn = currentUser && !this.state.loading;

        let content = this.props.children;
        if (isLoggedIn) {
            content = (
                <FirebaseSessionTracker user={currentUser}>
                    {this.renderPostAuth(currentUser, noVerify, (
                        popup && !skipReload
                            ? <ForceReload />
                            : content
                    ))}
                </FirebaseSessionTracker>
            );
        } else {
            RemoveSplashScreen();
            content = this.renderAuthContent();
        }

        if (popup) {
            content = (
                <Backdrop
                    invisible={!backdropVisible}
                    open={true}
                    onClick={e => {
                        window.top.postMessage("bai-auth-popup-close", window.location.origin);
                        onClose && onClose(e);
                    }}
                >
                    <div onClick={e => e.stopPropagation()}>
                        {content}
                    </div>
                </Backdrop>
            );
        }

        return (
            <GoogleRedirectHandler
                signInWithGoogleIdToken={idToken => this.handleGoogleIdToken(auth, idToken)}
                onError={error => this.setState({ error })}
            >
                <MicrosoftRedirectHandler
                    signInWithMicrosoftIdToken={customToken => this.handleMicrosoftIdToken(auth, customToken)}
                    onError={error => this.handleMicrosoftError(error)}
                >
                    {content}
                </MicrosoftRedirectHandler>
            </GoogleRedirectHandler>
        );
    }

    renderPostAuth(currentUser, noVerify, children) {
        RemoveSplashScreen();

        return (
            <Experiment id="must_verify_email">
                {isEnabled => {
                    if (isEnabled && !noVerify && !currentUser.emailVerified) {
                        return (
                            <VerifyEmail
                                email={currentUser.email}
                                noLogo={this.props.popup}
                            >
                                {children}
                            </VerifyEmail>
                        );
                    } else {
                        return children;
                    }
                }}
            </Experiment>
        );
    }

    renderAuthContent() {
        const { auth } = this.props;
        const { error } = this.state;

        let submitButtonLabel = null;

        if (this.state.page === "createAccount" && (this.props.reason === "team-invite" || this.props.reason === "presentation-invite")) {
            submitButtonLabel = "Create account";
        }

        switch (this.state.page) {
            case "signIn":
                return (
                    <SignIn
                        loading={this.state.loading}
                        error={error}
                        info={this.state.info}
                        emailError={this.state.emailError}
                        passwordError={this.state.passwordError}
                        defaultEmail={this.props.readonlyEmail}
                        email={this.state.email}
                        password={this.state.password}
                        showOnlyGoogle={this.props.showOnlyGoogle}
                        hero={this.hero()}
                        onClickSubmit={this.handleSignIn(auth)}
                        onClickGoogleButton={this.handleGoogle(auth)}
                        onClickMicrosoftButton={this.handleMicrosoft(auth)}
                        onClickForgot={this.handleClickForgot}
                        onClickSwitchLink={this.handleSwitchForm}
                        noToggle={
                            !!this.props.readonlyEmail ||
                            this.props.noToggle ||
                            this.state.ssoState === "strict"
                        }
                        ssoState={this.state.ssoState}
                        onEmailChange={this.handleEmailChange}
                        onPasswordChange={this.handlePasswordChange}
                        subheading={this.subheading()}
                        noLogo={this.props.popup}
                        hoistTerms={this.props.popup}
                        submitButtonLabel={submitButtonLabel}
                    />
                );
            case "createAccount":
                return (
                    <CreateAccount
                        loading={this.state.loading}
                        error={error}
                        info={this.state.info}
                        defaultEmail={this.props.readonlyEmail}
                        email={this.state.email}
                        password={this.state.password}
                        showOnlyGoogle={this.props.showOnlyGoogle}
                        withCompany={false}
                        onClickSubmit={this.handleCreateAccount(auth)}
                        onClickGoogleButton={this.handleGoogle(auth)}
                        onClickMicrosoftButton={this.handleMicrosoft(auth)}
                        onClickSwitchLink={this.handleSwitchForm}
                        hero={this.hero()}
                        noToggle={
                            !!this.props.readonlyEmail ||
                            this.props.noToggle ||
                            this.state.ssoState === "strict"
                        }
                        onEmailChange={this.handleEmailChange}
                        onPasswordChange={this.handlePasswordChange}
                        onFirstNameChange={this.handleFirstNameChange}
                        onLastNameChange={this.handleLastNameChange}
                        onCompanyChange={this.handleCompanyChange}
                        subheading={this.subheading()}
                        noLogo={this.props.popup}
                        hoistTerms={this.props.popup}
                        noTitle={!this.props.popup}
                        submitButtonLabel={submitButtonLabel}
                    />
                );
            case "forgotPassword":
                return (
                    <ResetPassword
                        email={this.state.email}
                        hero={this.hero()}
                        onClickReset={this.handleResetPassword(auth)}
                        emailError={this.state.emailError}
                        onEmailChange={email => this.setState({ email })}
                        noLogo={this.props.popup}
                    />
                );
            case "resetDone":
                return (
                    <ResetDone
                        hero={this.hero()}
                        noLogo={this.props.popup}
                    />
                );
            case "waitForGoogle":
                return (
                    <WaitForGoogle
                        hero={this.hero()}
                        noLogo={this.props.popup}
                    />
                );
            case "waitForMicrosoft":
                return (
                    <WaitForMicrosoft
                        hero={this.hero()}
                        noLogo={this.props.popup}
                    />
                );
            default:
                throw new Error();
        }
    }
}

// ugly hack to force popup page to reload inside main window
export function ForceReload() {
    const url = new URL(window.location.href);
    const search = new URLSearchParams(url.search);
    search.delete("popup");
    url.search = search.toString();
    window.top.location = url.href;
    return null;
}
